import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import Link from 'next/link'
import {useLinkResolver} from 'prismicio'
import type {ProductAnnouncementCardDocumentData} from 'prismicio-types'
import {Icon} from 'src/common/Icon'
import {LinkWrapper} from 'src/common/LinkWrapper'
import type {TrackingProps} from 'src/common/helpers'
import {EVENTS, track} from 'src/common/helpers'

type ProductAnnouncementCardProps = ProductAnnouncementCardDocumentData & {
  categoryLabel: string
  formattedDate: string
} & Omit<TrackingProps, 'event' | 'mktgId' | 'dataType'>

export const ProductAnnouncementCard = ({
  categoryLabel,
  formattedDate,
  description,
  image,
  link_label,
  link_url,
  title,
  location,
  pageName,
  hide_date,
}: ProductAnnouncementCardProps) => {
  const {linkResolver} = useLinkResolver()

  return (
    <div className="grid-base grid items-center gap-y-6 md:gap-y-8">
      <div className="col-span-full row-start-2 flex flex-col gap-6 max-md:pb-10 md:col-span-6 md:row-start-1 lg:gap-8">
        {(categoryLabel || !hide_date) && (
          <div className="flex items-center gap-4">
            <span className="text-button-s w-fit rounded-[4px] bg-[#E9F3FF] px-2.5 py-2 text-[#2C8FFF]">
              {categoryLabel}
            </span>
            <span className="text-button-s text-moss-grey-300">{formattedDate}</span>
          </div>
        )}
        <div className="flex flex-col gap-3">
          <LinkWrapper link={link_url} mktgId="product-announcement-title" location={location} pageName={pageName}>
            <h3 className="text-h3 text-moss-black">{title}</h3>
          </LinkWrapper>
          <p className="text-tg-body-l pb-2 text-moss-grey-400">{description}</p>
        </div>
        {isFilled.keyText(link_label) && (
          <Link
            href={linkResolver(link_url)}
            className="group relative flex h-[38px] w-fit items-center gap-[14px] rounded-[4px] border border-moss-grey-150 bg-moss-white pl-[14px] pr-[7px] hover:bg-moss-grey-100"
            onClick={() =>
              track({
                event: EVENTS.CLICK,
                mktgId: `product-announcement-button`,
                location: location,
                pageName: pageName,
                dataType: 'product-announcement',
              })
            }
          >
            <span className="text-button-s uppercase text-black">{link_label}</span>
            <div className="flex size-6 items-center justify-center rounded-sm bg-green-neon group-hover:bg-green-neon/[80%]">
              <Icon name="arrow" className="h-2 w-2.5 text-green-dark" />
            </div>
          </Link>
        )}
      </div>
      <LinkWrapper
        link={link_url}
        mktgId="product-announcement-image"
        location={location}
        pageName={pageName}
        className="col-span-full row-start-1 w-full md:col-span-5 md:col-start-8 md:row-start-1"
      >
        <PrismicNextImage
          field={image}
          quality={100}
          className="col-span-full row-start-1 aspect-[486/397] w-full max-w-[486px] rounded-lg border border-black/[10%] md:col-span-5 md:col-start-8 md:row-start-1"
        />
      </LinkWrapper>
    </div>
  )
}
